import currency from "currency.js";

import { createTransactionSummaryDataForCSV, downloadCSV } from "@/app/components/csv-components";
import {
  generateVintagesFromCommodityGenerationSummary,
  generateVintagesFromFormattedVintages,
} from "@/app/components/download-report-components/helpers";
import {
  TransactionSummaryData,
  TransactionSummaryTransactionContractProductAttributes,
} from "@/app/components/pdf-components/documents/transaction-summary/transaction-summary.types";
import { downloadReactPDFDocument } from "@/app/components/pdf-components/react-pdf-helpers";
import { AllRecOptions } from "@/app/hooks/use-all-rec-options";
import { formatAttributes } from "@/app/lib/format-attributes";
import { RECOrderAttributeOptions } from "@/app/lib/format-rec-options";
import { getTotalValue, reverseFeePercentageCalculation } from "@/app/lib/order-helpers";
import {
  OrderType,
  RecAttributes,
  RegistryCode,
  TradeContractStatus,
  TradeContractTransactionSummaryLazyQueryHookResult,
  TradeContractTransactionSummaryQuery,
  TradePosition,
} from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { SupportedDocumentType } from "../components/download-report-buttons";

export const tradeContractTransactionSummaryDataMapper = (
  data: TradeContractTransactionSummaryQuery,
  options: {
    recOptions: RECOrderAttributeOptions | undefined;
    recOptionsByRegistryCode: AllRecOptions["recOptionsByRegistryCode"];
    mergedRecOptions: AllRecOptions["mergedRecOptions"];
  },
  enableCrossRegistryForwardTrading: boolean,
): TransactionSummaryData => {
  const {
    tradeContractTransactionSummary: { tradeContract, tradeTransactions },
  } = data;
  const { recOptions, recOptionsByRegistryCode, mergedRecOptions } = options;

  const orderFormattedAttributes = formatAttributes({
    attributes: tradeContract.attributes as RecAttributes,
    position: TradePosition.Bid,
    options: enableCrossRegistryForwardTrading
      ? recOptionsByRegistryCode[tradeContract.registryCode as TraceXSupportedRegistries] ?? mergedRecOptions
      : recOptions,
  });

  const transactions = tradeTransactions.map((transaction) => {
    const total = currency(transaction.unitPrice).multiply(transaction.volume).value;
    const feePercentage = reverseFeePercentageCalculation(transaction.transactionFee, total);

    const productAttributes = transaction.transactionDetails?.map(
      (detail): TransactionSummaryTransactionContractProductAttributes => {
        const { volume, trackingSystem, attributes, commodityGenerationSummary, generator, registryCode } = detail;

        const transactionFormattedAttributes = formatAttributes({
          attributes: attributes as RecAttributes,
          position: TradePosition.Bid,
          options: enableCrossRegistryForwardTrading
            ? recOptionsByRegistryCode[registryCode as TraceXSupportedRegistries] ?? mergedRecOptions
            : recOptions,
        });

        const vintages =
          registryCode === RegistryCode.Ercot
            ? generateVintagesFromFormattedVintages(transactionFormattedAttributes.vintages)
            : generateVintagesFromCommodityGenerationSummary(commodityGenerationSummary ?? []);

        return {
          volume,
          trackingSystem,
          eligibilities: transactionFormattedAttributes.eligibilities,
          fuelSources: transactionFormattedAttributes.fuelSources,
          vintages,
          projects: generator?.name ? [generator.name] : [],
          locations: transactionFormattedAttributes.locations,
          certificationScheme: {
            certifications: transactionFormattedAttributes.certifications,
            crsEffectiveDate: generator?.crsEffectiveDate ?? "",
            crsExpiryDate: generator?.crsExpiryDate ?? "",
            commencementDate: (attributes as RecAttributes)?.commencementDate ?? "",
          },
          certificateTypes: transactionFormattedAttributes.certificateTypes,
        };
      },
    );

    return {
      contractId: transaction.transactionNumber ?? "",
      marketplace: "TraceX USA",
      participantName: tradeContract.account.company?.businessName ?? "",
      counterPartyName: tradeContract.counterAccount?.businessName ?? "",
      position: transaction.position,
      matchedDate: transaction.matchedDate,
      completedDate: transaction.completedDate,
      price: transaction.unitPrice,
      volume: transaction.volume,
      totalContractValue: getTotalValue(
        transaction.unitPrice,
        transaction.volume,
        tradeContract.position,
        feePercentage / 100,
      ).value,
      transactionFeeAmount: transaction.transactionFee,
      transactionFeePercentage: feePercentage,
      productAttributes,
      contractedDeliveryDate: transaction.contractedDeliveryDate,
      contractedPaymentDate: transaction.contractedPaymentDate,
    };
  });

  const totalOrderValue =
    tradeContract.status === TradeContractStatus.Withdrawn && transactions.length === 0
      ? currency(tradeContract.unitPrice).multiply(tradeContract.volume).value
      : transactions.reduce((acc, transaction) => currency(acc).add(transaction.totalContractValue).value, 0);

  return {
    order: {
      orderNumber: tradeContract.tradeContractNumber,
      orderDate: tradeContract.createdDate,
      marketplace: "TraceX USA",
      participantName: tradeContract.account.company?.businessName ?? "",
      counterPartyName: tradeContract.counterAccount?.businessName ?? "",
      position: tradeContract.position,
      type: OrderType.Forward,
      volume: tradeContract.volume,
      price: +tradeContract.unitPrice,
      assetType: tradeContract.assetType,
      totalOrderValue,
      currencyCode: tradeContract.currencyCode,
      attributes: {
        eligibilities: orderFormattedAttributes.eligibilities,
        fuelSources: orderFormattedAttributes.fuelSources,
        vintages: orderFormattedAttributes.vintages,
        locations: orderFormattedAttributes.locations,
        certifications: orderFormattedAttributes.certifications,
        certificateTypes: orderFormattedAttributes.certificateTypes,
      },
      status: tradeContract.status,
    },
    transactions,
  };
};

export const downloadTransactionSummaryReport = async (
  type: SupportedDocumentType,
  orderId: string,
  orderNumber: string,
  options: {
    recOptions: RECOrderAttributeOptions | undefined;
    recOptionsByRegistryCode: AllRecOptions["recOptionsByRegistryCode"];
    mergedRecOptions: AllRecOptions["mergedRecOptions"];
  },
  transactionSummaryQuery: TradeContractTransactionSummaryLazyQueryHookResult[0],
  activeRegistryCode: AggregatedRegistryCode,
  enableCrossRegistryForwardTrading: boolean,
  pdfRenderer: (data: TransactionSummaryData) => React.ReactElement,
) => {
  const { data, error } = await transactionSummaryQuery({
    variables: { tradeContractId: orderId, registryCode: activeRegistryCode },
  });

  if (data) {
    const transactionSummaryData = tradeContractTransactionSummaryDataMapper(
      data,
      options,
      enableCrossRegistryForwardTrading,
    );
    if (type === "pdf") {
      return downloadReactPDFDocument(
        `forward_trade_transaction_summary_${orderNumber}.pdf`,
        pdfRenderer(transactionSummaryData),
      );
    }
    if (type === "csv") {
      return downloadCSV(
        `forward_trade_transaction_summary_${orderNumber}.csv`,
        createTransactionSummaryDataForCSV({
          transactionSummary: transactionSummaryData,
          tradeType: "Forward",
          rowRegistryCode: activeRegistryCode,
        }),
      );
    }
  } else {
    throw error;
  }
};
